import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeSlash, CheckCircle, CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, tokenStore, pendingRegistrationStore } from '../lib/api';
import LegalAccordion from '../components/LegalAccordion';
import {
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
  SMS_TERMS,
  SMS_CONSENT_LABEL,
  CONSENT_VERSION,
} from '../lib/legalContent';
import rippersClubLogo from '../assets/rippers-club-logo.png';

const USTA_RATINGS = ['3.0', '3.5', '4.0', '4.5', '5.0'];
const HERO_BASE = 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8';
const HERO_LARGE = `${HERO_BASE}?auto=format&fit=crop&w=2400&q=80`;
const HERO_SMALL = `${HERO_BASE}?auto=format&fit=crop&w=900&q=70`;

/* Same themed shell as LoginPage / OnboardPage. Re-extracted locally so
   we don't have to refactor those — minimal blast radius. */
function ThemedShell({ children }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.src = HERO_LARGE;
    img.onload = () => setImgLoaded(true);
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#013523]">
      <picture
        aria-hidden="true"
        className={`fixed inset-0 transition-opacity duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
      >
        <source media="(min-width: 768px)" srcSet={HERO_LARGE} />
        <img src={HERO_SMALL} alt="" className="absolute inset-0 w-full h-full object-cover object-center" loading="eager" />
      </picture>
      <div aria-hidden="true" className="fixed inset-0 bg-black/55" />
      <div aria-hidden="true" className="fixed inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
      <div
        aria-hidden="true"
        className="fixed inset-0"
        style={{ background: 'radial-gradient(120% 80% at 50% 35%, transparent 0%, rgba(0,0,0,0.35) 80%)' }}
      />

      <div className="relative min-h-screen flex flex-col items-center px-5 py-10">
        <div className="flex flex-col items-center mb-6 sm:mb-8" data-testid="join-logo">
          <img
            src={rippersClubLogo}
            alt="The The Rippers Club"
            className="w-56 sm:w-64 h-auto drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)]"
          />
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls =
  'w-full bg-white/15 border border-white/25 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#c2a251]/60 focus:border-[#c2a251]/60 transition-all';
const labelCls = 'text-[11px] font-sans tracking-[0.18em] uppercase text-white/70 mb-1.5 block';

export default function JoinPage() {
  const navigate = useNavigate();
  const { user, hasLadderAccess, login } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ustaRating, setUstaRating] = useState('');
  const [gender, setGender] = useState('');
  const [showPw, setShowPw] = useState(false);

  const [smsConsent, setSmsConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsTermsOpen, setSmsTermsOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  if (user && hasLadderAccess) return <Navigate to="/" replace />;

  // SMS controls follow phone-number presence. A2P 10DLC compliance: we can't
  // collect SMS consent (let alone send a message) without a phone number.
  const hasPhone = phone.trim().length > 0;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setFormError('');

    if (!firstName.trim() || !lastName.trim()) return setFormError('First and last name are required.');
    if (!email.trim()) return setFormError('Email is required.');
    if (!gender) return setFormError('Please select your gender.');
    if (password.length < 6) return setFormError('Password must be at least 6 characters.');
    if (password !== confirmPassword) return setFormError('Passwords do not match.');
    if (!termsAccepted) return setFormError('You must accept the Terms & Conditions and Privacy Policy.');

    setLoading(true);
    try {
      const res = await authAPI.register({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        password,
        usta_rating: ustaRating || null,
        gender,
        sms_consent: hasPhone && smsConsent,
        terms_accepted: true,
        consent_version: CONSENT_VERSION,
      });
      // Persist session token returned from registration so the pending screen
      // can identify the user without forcing a fresh sign-in.
      if (res.data?.session_token) tokenStore.set(res.data.session_token);
      // Cache the registrant's first name so /registration-pending continues
      // to greet them by name even after they tap "Sign out".
      pendingRegistrationStore.set({ first_name: firstName.trim() });
      login(res.data.user, res.data.player, res.data.has_ladder_access, res.data.profile_completed);
      toast.success('Registration submitted!');
      navigate('/registration-pending', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setFormError(typeof detail === 'string' ? detail : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemedShell>
      <div
        data-testid="join-page"
        className="w-full max-w-md rounded-3xl p-6 sm:p-7 space-y-5 backdrop-blur-xl bg-white/10 border border-white/20 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
      >
        <div>
          <h2 className="font-serif text-2xl text-white tracking-tight">Join The Rippers Club</h2>
          <p className="text-xs text-white/70 mt-0.5">
            Create your account and request access. Account approval required.
          </p>
        </div>

        {formError && (
          <div data-testid="join-error" className="bg-red-500/15 text-red-200 text-sm rounded-xl px-4 py-3 border border-red-400/30">
            {formError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="join-first-name" className={labelCls}>First Name *</label>
              <input
                id="join-first-name" data-testid="join-first-name"
                type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                className={inputCls} placeholder="John" required
              />
            </div>
            <div>
              <label htmlFor="join-last-name" className={labelCls}>Last Name *</label>
              <input
                id="join-last-name" data-testid="join-last-name"
                type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                className={inputCls} placeholder="Smith" required
              />
            </div>
          </div>

          <div>
            <label htmlFor="join-email" className={labelCls}>Email *</label>
            <input
              id="join-email" data-testid="join-email"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputCls} placeholder="your@email.com" required
            />
          </div>

          <div>
            <label htmlFor="join-phone" className={labelCls}>Phone Number</label>
            <input
              id="join-phone" data-testid="join-phone"
              type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              className={inputCls} placeholder="(555) 123-4567"
            />
            <p className="text-[10px] text-white/55 mt-1.5" data-testid="join-phone-helper">
              Optional. Add a phone number to receive SMS notifications.
            </p>

            {/* OPTIONAL SMS consent checkbox. SMS is NOT required to create or use an
                account. Box is intentionally unchecked by default; user must affirmatively
                opt in. The card is disabled until a phone number is entered, because
                consenting to SMS without a destination number isn't meaningful (A2P 10DLC). */}
            <div
              data-testid="join-sms-card"
              className={`mt-3 rounded-xl border border-white/15 bg-white/5 p-3 space-y-3 ${
                hasPhone ? '' : 'opacity-60'
              }`}
              aria-disabled={!hasPhone}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#c2a251] font-semibold">
                  SMS Notifications
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/55">
                  Optional
                </span>
              </div>

              {hasPhone ? (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    data-testid="join-sms-consent"
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-white/30 bg-white/10 text-[#c2a251] focus:ring-[#c2a251]/30"
                  />
                  <span className="text-[11px] leading-relaxed text-white/85">
                    {SMS_CONSENT_LABEL}
                  </span>
                </label>
              ) : (
                <p
                  data-testid="join-sms-disabled-helper"
                  className="text-[11px] leading-relaxed text-white/65"
                >
                  Enter a phone number to enable optional SMS notifications.
                </p>
              )}

              <button
                type="button"
                onClick={() => setSmsTermsOpen((v) => !v)}
                aria-expanded={smsTermsOpen}
                data-testid="join-sms-terms-toggle"
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#c2a251] hover:text-[#e9d18a]"
              >
                <span>{smsTermsOpen ? 'Hide' : 'View'} SMS Terms</span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${smsTermsOpen ? 'rotate-180' : ''}`} />
              </button>

              {smsTermsOpen && (
                <div data-testid="join-sms-terms-inline" className="text-[11px] leading-relaxed text-white/75 space-y-3 pt-2 border-t border-white/10">
                  {SMS_TERMS.map((s, i) => (
                    <div key={i} className="space-y-1">
                      {s.heading && <h4 className="font-serif text-white text-[12px]">{s.heading}</h4>}
                      {s.intro && <p>{s.intro}</p>}
                      {s.bullets && (
                        <ul className="list-disc pl-4 space-y-1 marker:text-white/40">
                          {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                        </ul>
                      )}
                      {s.body && <p>{s.body}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="join-password" className={labelCls}>Password *</label>
            <div className="relative">
              <input
                id="join-password" data-testid="join-password"
                type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                className={`${inputCls} pr-12`} placeholder="At least 6 characters" required minLength={6}
              />
              <button
                type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                aria-label={showPw ? 'Hide password' : 'Show password'} tabIndex={-1}
              >
                {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="join-confirm-password" className={labelCls}>Confirm Password *</label>
            <input
              id="join-confirm-password" data-testid="join-confirm-password"
              type={showPw ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputCls} placeholder="Re-enter your password" required minLength={6}
            />
          </div>

          <div>
            <label htmlFor="join-usta-rating" className={labelCls}>USTA Rating</label>
            <select
              id="join-usta-rating" data-testid="join-usta-rating"
              value={ustaRating} onChange={(e) => setUstaRating(e.target.value)}
              className={`${inputCls} appearance-none`}
            >
              <option value="" className="text-[#013523]">Select rating…</option>
              {USTA_RATINGS.map((r) => (
                <option key={r} value={r} className="text-[#013523]">{r}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="join-gender" className={labelCls}>Gender *</label>
            <select
              id="join-gender" data-testid="join-gender"
              value={gender} onChange={(e) => setGender(e.target.value)}
              className={`${inputCls} appearance-none`}
              required
            >
              <option value="" className="text-[#013523]">Select…</option>
              <option value="male" className="text-[#013523]">Male</option>
              <option value="female" className="text-[#013523]">Female</option>
            </select>
            <p className="text-[10px] text-white/55 mt-1.5">
              Required to register for Men&apos;s Night or Ladies Night events.
            </p>
          </div>

          {/* Required terms acceptance */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="join-terms-accepted"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-white/30 bg-white/10 text-[#c2a251] focus:ring-[#c2a251]/30"
            />
            <span className="text-[11px] leading-relaxed text-white/85">
              I have read and agree to The Rippers Club's{' '}
              <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link> and{' '}
              <Link to="/terms" className="underline hover:text-white">Terms &amp; Conditions</Link>.
            </span>
          </label>

          <button
            data-testid="join-submit"
            type="submit" disabled={loading}
            className="w-full bg-[#015e3f] hover:bg-[#014d34] active:scale-[0.99] transition-transform text-white rounded-full px-6 py-3 font-medium shadow-[0_8px_24px_rgba(1,94,63,0.5)] disabled:opacity-50"
          >
            {loading ? 'Submitting…' : 'Request Access'}
          </button>
        </form>

        <p className="text-center text-sm text-white/70">
          Already have an account?{' '}
          <Link to="/login" className="text-white font-medium hover:underline" data-testid="join-signin-link">
            Sign in
          </Link>
        </p>

        {/* Full legal accordions — full text on-page per spec */}
        <div className="space-y-2 pt-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/55 mb-1">Legal</p>
          <LegalAccordion title="Privacy Policy" sections={PRIVACY_POLICY} testId="join-accordion-privacy" />
          <LegalAccordion title="Terms & Conditions" sections={TERMS_AND_CONDITIONS} testId="join-accordion-terms" />
          <LegalAccordion title="SMS Messaging Terms" sections={SMS_TERMS} testId="join-accordion-sms" />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 text-[11px] text-white/60">
        <Link to="/privacy" className="hover:text-white/90" data-testid="join-footer-privacy">Privacy Policy</Link>
        <span aria-hidden="true" className="text-white/30">•</span>
        <Link to="/terms" className="hover:text-white/90" data-testid="join-footer-terms">Terms</Link>
        <span aria-hidden="true" className="text-white/30">•</span>
        <Link to="/sms-terms" className="hover:text-white/90" data-testid="join-footer-sms-terms">SMS Terms</Link>
      </div>

      <p className="mt-3 max-w-sm text-center text-[10px] leading-relaxed text-white/45">
        SMS notifications are optional. If you enable them, you may receive text messages
        related to your ladder activity, including challenges, match updates, and reminders.
        Message frequency varies. Message and data rates may apply. Reply STOP to opt out
        and HELP for help. Consent is not required to use The Rippers Club.
      </p>
    </ThemedShell>
  );
}
